// src/features/giftCategoriesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch } from "../app/store";
import axios from "axios";

/* ------------ Types ------------ */
type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";


export interface GiftCategory {
  id: string;
  name: string;
  description: string;
  min_range: number;
  max_range: number;
  currency: string;
  created_at: string; // ISO8601
  updated_at: string; // ISO8601
}

export interface ValidationErrorItem {
  loc: (string | number)[];
  msg: string;
  type: string;
}

/* ------------ State ------------ */
interface GiftCategoriesState {
  // list
  listStatus: AsyncStatus;
  listError: string | null;
  isListInProgress: boolean;
  categories: GiftCategory[];
}

const initialState: GiftCategoriesState = {
  // list
  listStatus: "idle",
  listError: null,
  isListInProgress: false,
  categories: [],
};

/* ------------ Error helper ------------ */
function formatApiError(err: any): {
  status: number | null;
  message: string;
  validation?: ValidationErrorItem[];
} {
  if (err?.response) {
    const status = err.response.status as number;
    const data = err.response.data;

    // 422 validation array
    if (status === 422 && Array.isArray(data?.detail)) {
      const items = data.detail as ValidationErrorItem[];
      const msg = items.map(i => `${i.loc?.join(".")}: ${i.msg}`).join("; ");
      return { status, message: msg || "Validation failed", validation: items };
    }

    // 400/401/403/... with string detail or message
    const detail = data?.detail || data?.message || "Request failed";
    return { status, message: String(detail) };
  }

  return { status: null, message: "Network error. Please try again." };
}

/* ------------ Thunks ------------ */
export const fetchGiftCategories = createAsyncThunk<
  GiftCategory[],
  void,
  { dispatch: AppDispatch; rejectValue: { message: string } }
>("giftCategories/fetchGiftCategories", async (_void, { rejectWithValue }) => {
  try {
    const res = await axios.get("/categories/list");
    return (res.data as GiftCategory[]) ?? [];
  } catch (error: any) {
    const { message } = formatApiError(error);
    return rejectWithValue({ message });
  }
});


/* ------------ Slice ------------ */
const giftCategoriesSlice = createSlice({
  name: "giftCategories",
  initialState,
  reducers: {
    // list resets
    resetListStatus(state) {
      state.listStatus = "idle";
      state.listError = null;
      state.isListInProgress = false;
    },
    clearCategories(state) {
      state.categories = [];
    },
  },
  extraReducers: (builder) => {
    // fetchGiftCategories
    builder
      .addCase(fetchGiftCategories.pending, (state) => {
        state.listStatus = "loading";
        state.listError = null;
        state.isListInProgress = true;
      })
      .addCase(fetchGiftCategories.fulfilled, (state, action: PayloadAction<GiftCategory[]>) => {
        state.listStatus = "succeeded";
        state.isListInProgress = false;
        state.categories = action.payload || [];
      })
      .addCase(fetchGiftCategories.rejected, (state, action) => {
        state.listStatus = "failed";
        state.isListInProgress = false;
        state.listError = action.payload?.message || action.error.message || "Failed to fetch gift categories";
      });
  },
});

/* ------------ Exports (no selectors) ------------ */
export const {
  // list
  resetListStatus,
  clearCategories,
} = giftCategoriesSlice.actions;

export default giftCategoriesSlice.reducer;
